import { format } from '@rental/i18n'
import { Button, Checkbox, FilePicker, Notice } from '@rental/ui'
import { type ChangeEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { Dialog } from '../../components/Dialog'
import { ReasonList } from '../../components/SubPanel'
import { useLocale } from '../../i18n'
import { InvalidImage, prepareImage } from './image'
import { type DocumentScanEngine, ScanCancelled, type ScanImage } from './ocr-interface'
import {
  type DraftPatch,
  draftPatch,
  type PatchField,
  type ReviewDecisions,
  type ReviewRow,
  type RowLabel,
  reviewRows,
  scanEmpty,
  unreadFields,
  unverified,
} from './review'
import { runScan, type ScanOutcome, ScanTimeout } from './run-scan'
import type { LicenceRule } from './scan-config'

type Phase = 'capture' | 'reading' | 'review'
/** What went wrong on the way to a read, said in the capture step with the way on. */
type Problem = 'failed' | 'timeout' | 'invalidFile'
type Camera = 'starting' | 'on' | 'off'

/**
 * The scan flow as one dialog (OPS-1): capture the document with the camera or
 * choose a photo of it (front, and optionally the back for a US licence), read
 * it in the browser, then let the operator check the read fields and confirm.
 * Nothing is applied until Confirm; manual entry is always one tap away; a
 * read in progress can be called off. The image is held in memory only and
 * dropped when the dialog closes — never stored, never sent.
 *
 * One read at a time, tied to this dialog: a run has an id and a signal, its
 * answer is read only while it is the current run, and cancel, retake and the
 * dialog's close all stop the engine (ScanCancelled is nobody's error).
 *
 * The OCR engine is injected for tests; in the app it is the browser engine,
 * imported lazily so Tesseract/ZXing stay out of the main bundle.
 */
export function ScanDialog({
  onApply,
  onClose,
  engine,
  current,
  returnDate,
  licence,
}: {
  onApply: (patch: DraftPatch) => void
  onClose: () => void
  engine?: DocumentScanEngine
  /** What the form holds now: a read value replaces it only when the operator says so. */
  current: DraftPatch
  /** ISO date the vehicle is due back; the licence must still be valid then. */
  returnDate?: string
  /** The booked category's licence rule, for the (warn-only) category check. */
  licence?: LicenceRule
}) {
  const { messages: m } = useLocale()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const run = useRef<{ id: number; controller: AbortController } | null>(null)
  const runs = useRef(0)
  const [camera, setCamera] = useState<Camera>('starting')
  const [phase, setPhase] = useState<Phase>('capture')
  const [problem, setProblem] = useState<Problem | null>(null)
  const [images, setImages] = useState<ScanImage[]>([])
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null)
  const [verified, setVerified] = useState<ReadonlySet<RowLabel>>(() => new Set())
  const [replace, setReplace] = useState<ReadonlySet<PatchField>>(() => new Set())
  const reasonsId = useId()

  // Open the modal and start the camera; on the way out stop its tracks and any read.
  useEffect(() => {
    dialogRef.current?.showModal()
    let gone = false
    if (typeof navigator.mediaDevices?.getUserMedia !== 'function') {
      setCamera('off')
    } else {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: 'environment' } })
        .then((stream) => {
          if (gone) {
            for (const track of stream.getTracks()) track.stop()
            return
          }
          streamRef.current = stream
          if (videoRef.current) videoRef.current.srcObject = stream
          setCamera('on')
        })
        .catch(() => setCamera('off'))
    }
    return () => {
      gone = true
      run.current?.controller.abort()
      run.current = null
      for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    }
  }, [])

  // The preview is drawn again after a review or a problem: the stream goes back on it.
  const attachVideo = (video: HTMLVideoElement | null) => {
    videoRef.current = video
    if (video && streamRef.current && video.srcObject !== streamRef.current) {
      video.srcObject = streamRef.current
    }
  }

  async function add(file: Blob) {
    setProblem(null)
    try {
      const blob = await prepareImage(file)
      setImages((list) => [...list, { blob, side: list.length === 0 ? 'front' : 'back' }])
    } catch (error) {
      setProblem(error instanceof InvalidImage ? 'invalidFile' : 'failed')
    }
  }

  async function capture() {
    const video = videoRef.current
    if (!video || video.videoWidth === 0) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92),
    )
    if (blob) await add(blob)
  }

  async function chose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // The same file may be chosen again after a retake.
    event.target.value = ''
    if (file) await add(file)
  }

  async function read() {
    if (images.length === 0 || run.current) return
    const controller = new AbortController()
    const id = ++runs.current
    run.current = { id, controller }
    setProblem(null)
    setPhase('reading')
    try {
      // The engine is lazy (Tesseract/ZXing); runScan is pure given one.
      const scanner = engine ?? (await import('./tesseract-engine')).createTesseractEngine()
      const result = await runScan(
        scanner,
        { images, returnDate, licence },
        { signal: controller.signal },
      )
      if (run.current?.id !== id) return
      setOutcome(result)
      setVerified(new Set())
      setReplace(new Set())
      setPhase('review')
    } catch (error) {
      if (run.current?.id !== id || error instanceof ScanCancelled) return
      setProblem(error instanceof ScanTimeout ? 'timeout' : 'failed')
      setPhase('capture')
    } finally {
      if (run.current?.id === id) run.current = null
    }
  }

  function stopRun() {
    run.current?.controller.abort()
    run.current = null
  }

  function cancelRead() {
    stopRun()
    setPhase('capture')
  }

  function retake() {
    stopRun()
    setImages([])
    setOutcome(null)
    setProblem(null)
    setPhase('capture')
  }

  function confirm() {
    if (outcome) onApply(draftPatch(outcome, current, { verified, replace }))
    onClose()
  }

  const rows = outcome ? reviewRows(outcome, current) : []
  const decisions: ReviewDecisions = { verified, replace }
  const pending = unverified(rows, decisions)
  const nothing = outcome ? scanEmpty(outcome) : false
  const partial = outcome ? !nothing && unreadFields(outcome).length > 0 : false
  const sideLabel = (side: ScanImage['side']) => (side === 'front' ? m.scan.front : m.scan.back)

  return (
    <Dialog dialogRef={dialogRef} title={m.scan.title} onClose={onClose}>
      <div className="flex max-w-xl flex-col gap-4">
        {phase === 'capture' ? (
          <>
            <p className="text-sm text-text-muted">{m.scan.intro}</p>
            {problem ? <Notice tone="warning">{m.scan[problem]}</Notice> : null}
            {camera === 'off' ? <Notice tone="warning">{m.scan.cameraOff}</Notice> : null}
            {camera !== 'off' ? (
              <div className="relative overflow-hidden rounded-card bg-mz-neutral-900">
                {/* A live preview, muted: it carries no sound and needs no captions. */}
                <video ref={attachVideo} autoPlay playsInline muted className="w-full" />
                {/* The card guide: a document fills this frame. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-6 rounded-sm border-2 border-dashed border-surface"
                />
              </div>
            ) : null}
            {images.length > 0 ? (
              <ul className="flex flex-col gap-1 text-sm">
                {images.map((image) => (
                  <li key={image.side}>
                    {format(m.scan.captured, { side: sideLabel(image.side) })}
                  </li>
                ))}
              </ul>
            ) : null}
            {images.length < 2 ? (
              <p className="text-xs text-text-muted">
                {images.length === 0 ? m.scan.front : m.scan.back}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              {camera === 'on' && images.length < 2 ? (
                <Button onClick={() => void capture()}>{m.scan.capture}</Button>
              ) : null}
              {images.length < 2 ? (
                <FilePicker
                  label={m.scan.choosePhoto}
                  accept="image/*"
                  onChange={(event) => void chose(event)}
                />
              ) : null}
              {images.length > 0 ? (
                <Button onClick={() => void read()}>{m.scan.readAction}</Button>
              ) : null}
              {images.length > 0 ? (
                <Button variant="outline" onClick={retake}>
                  {m.scan.retake}
                </Button>
              ) : null}
              <Button variant="outline" onClick={onClose}>
                {m.scan.useManual}
              </Button>
            </div>
          </>
        ) : null}

        {phase === 'reading' ? (
          <>
            <p role="status" className="text-sm">
              {m.scan.reading}
            </p>
            <div>
              <Button variant="outline" onClick={cancelRead}>
                {m.scan.cancel}
              </Button>
            </div>
          </>
        ) : null}

        {phase === 'review' && outcome ? (
          <>
            {nothing ? (
              <Notice tone="warning">{m.scan.nothingRead}</Notice>
            ) : (
              <>
                <p className="text-sm text-text-muted">{m.scan.reviewIntro}</p>
                {partial ? <Notice tone="warning">{m.scan.partial}</Notice> : null}
                <ReviewList
                  rows={rows}
                  decisions={decisions}
                  onVerify={(label, on) =>
                    setVerified((set) => {
                      const next = new Set(set)
                      if (on) next.add(label)
                      else next.delete(label)
                      return next
                    })
                  }
                  onReplace={(field, on) =>
                    setReplace((set) => {
                      const next = new Set(set)
                      if (on) next.add(field)
                      else next.delete(field)
                      return next
                    })
                  }
                />
                {outcome.issues.map((issue) => (
                  <Notice key={issue.code} tone={issue.severity === 'error' ? 'danger' : 'warning'}>
                    {m.scan.issue[issue.code]}
                  </Notice>
                ))}
                {outcome.category === 'mismatch' ? (
                  <Notice tone="warning">{m.scan.categoryWarning}</Notice>
                ) : null}
              </>
            )}
            <div className="flex flex-wrap gap-3">
              {!nothing ? (
                <Button
                  onClick={confirm}
                  disabled={pending.length > 0}
                  aria-describedby={pending.length > 0 ? reasonsId : undefined}
                >
                  {m.scan.confirm}
                </Button>
              ) : null}
              <Button variant="outline" onClick={retake}>
                {m.scan.retake}
              </Button>
              <Button variant="outline" onClick={onClose}>
                {m.scan.useManual}
              </Button>
            </div>
            <ReasonList id={reasonsId} reasons={pending.length > 0 ? [m.scan.mustVerify] : []} />
          </>
        ) : null}
      </div>
    </Dialog>
  )
}

function ReviewList({
  rows,
  decisions,
  onVerify,
  onReplace,
}: {
  rows: readonly ReviewRow[]
  decisions: ReviewDecisions
  onVerify: (label: RowLabel, on: boolean) => void
  onReplace: (field: PatchField, on: boolean) => void
}): ReactNode {
  const { messages: m } = useLocale()
  // Place rows show the read name under the form's place label; the operator
  // still resolves the code via the PlacePicker (the scan never guesses one).
  // A field named by its group on the form ("Numero" under "Patente") is named
  // with the group here, where no group stands around it.
  const labelOf = (label: RowLabel): string => {
    switch (label) {
      case 'birthPlaceName':
        return m.identity.birthPlace
      case 'citizenshipName':
        return m.identity.citizenship
      case 'documentType':
        return `${m.identity.document} · ${m.identity.documentType}`
      case 'licenceNumber':
        return `${m.identity.licence} · ${m.identity.licenceNumber}`
      default:
        return m.identity[label]
    }
  }
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li
          key={row.label}
          className={
            row.needsCheck
              ? 'rounded-sm border border-warning bg-surface-well p-2'
              : 'rounded-sm border border-rule p-2'
          }
        >
          <span className="block text-xs text-text-muted">
            {labelOf(row.label)} · {m.scan.readFrom}
            {row.needsCheck ? ` · ${m.scan.toCheck}` : ''}
          </span>
          <span className="figures">{row.value}</span>
          {row.current ? (
            <>
              <span className="block text-xs text-text-muted">
                {m.scan.current}: <span className="figures">{row.current}</span>
              </span>
              <Checkbox
                label={m.scan.replace}
                aria-label={`${m.scan.replace}: ${labelOf(row.label)}`}
                checked={decisions.replace.has(row.label as PatchField)}
                onChange={(event) => onReplace(row.label as PatchField, event.target.checked)}
              />
            </>
          ) : null}
          {row.needsCheck ? (
            <Checkbox
              label={m.scan.verified}
              aria-label={`${m.scan.verified}: ${labelOf(row.label)}`}
              checked={decisions.verified.has(row.label)}
              onChange={(event) => onVerify(row.label, event.target.checked)}
            />
          ) : null}
        </li>
      ))}
    </ul>
  )
}
